import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface FilterOption {
  /** What the filter matches on ('' = blank cells). */
  key: string;
  /** What the list shows. */
  label: string;
}

/**
 * An Excel-style column filter: a ▾ in the header cell opens the column's
 * values with tick boxes, a search box and (Select All).  OK applies; Cancel,
 * Esc or a click outside leaves the filter as it was.  The ▾ turns into a
 * funnel while the column is filtered.
 *
 * `options` should be the values in the rows that pass the OTHER columns'
 * filters, as Excel lists them.  `selected` null means not filtered.
 *
 * The list is drawn in a portal with fixed position, so a table's scrolling
 * container can't clip it.
 */
export function ColumnFilter({ label, options, selected, onChange, numeric }: {
  label: string;
  options: FilterOption[];
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  /** Sort the values as numbers (Length, GSM …). */
  numeric?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const allRef = useRef<HTMLInputElement>(null);
  const active = selected != null;

  // Blanks last; numbers as numbers; text A→Z ignoring case.
  const sorted = useMemo(() => [...options].sort((a, b) => {
    if (a.key === '' || b.key === '') return a.key === '' ? (b.key === '' ? 0 : 1) : -1;
    return numeric ? Number(a.key) - Number(b.key)
      : a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
  }), [options, numeric]);

  const term = search.trim().toLowerCase();
  const shown = term ? sorted.filter(o => (o.key === '' ? '(blanks)' : o.label.toLowerCase()).includes(term)) : sorted;
  const allShown = shown.length > 0 && shown.every(o => draft.has(o.key));
  const someShown = shown.some(o => draft.has(o.key));
  // With a search, OK keeps just the ticked search results — as Excel does.
  const picked = term ? shown.filter(o => draft.has(o.key)).map(o => o.key) : sorted.filter(o => draft.has(o.key)).map(o => o.key);

  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someShown && !allShown;
  });

  /**
   * Under the ▾, kept on screen: flipped above it when there's no room below,
   * and moved back inside the window's right edge.
   */
  function place() {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 240;
    const height = panelRef.current?.offsetHeight ?? 320;
    const below = r.bottom + 4;
    const top = below + height <= window.innerHeight - 8 ? below : Math.max(8, r.top - height - 4);
    setPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
  }

  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => !!t && (panelRef.current?.contains(t as Node) || btnRef.current?.contains(t as Node));
    const onDown = (e: MouseEvent) => { if (!inside(e.target)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Follow the ▾ rather than close: a phone's keyboard opening for the
    // search box resizes the window, and closing then would lose the list.
    let frame = 0;
    const follow = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    follow();   // again once the list has its real height
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  function toggleOpen() {
    if (open) { setOpen(false); return; }
    place();
    setSearch('');
    setDraft(new Set(selected ?? sorted.map(o => o.key)));
    setOpen(true);
  }

  function toggleAll() {
    const next = new Set(draft);
    for (const o of shown) (allShown ? next.delete(o.key) : next.add(o.key));
    setDraft(next);
  }

  function toggle(key: string) {
    const next = new Set(draft);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDraft(next);
  }

  function apply() {
    // Everything ticked is the same as no filter.
    const everything = !term && sorted.every(o => draft.has(o.key));
    onChange(everything ? null : new Set(picked));
    setOpen(false);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
        title={active ? `${label} is filtered — click to change` : `Filter ${label}`}
        className={`ml-1 inline-flex h-4 w-4 items-center justify-center rounded align-middle ${
          active ? 'bg-brand text-white' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'
        }`}
      >
        {active ? (
          <svg viewBox="0 0 16 16" width="10" height="10" fill="currentColor" aria-hidden="true">
            <path d="M1 2h14l-5.5 6.5V14l-3-1.5V8.5z" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" width="10" height="10" fill="currentColor" aria-hidden="true">
            <path d="M3 6l5 5 5-5z" />
          </svg>
        )}
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={`Filter ${label}`}
          className="fixed z-[60] w-60 rounded-lg border border-slate-200 bg-white p-2 text-xs normal-case tracking-normal text-slate-700 shadow-xl"
          style={{ top: pos.top, left: pos.left }}
        >
          <input
            autoFocus
            className="input mb-2 px-2 py-1 text-xs"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && picked.length) { e.preventDefault(); apply(); } }}
          />
          <div className="max-h-56 overflow-y-auto rounded border border-slate-100">
            <label className="flex cursor-pointer items-center gap-2 px-2 py-1 font-medium hover:bg-slate-50">
              <input ref={allRef} type="checkbox" checked={allShown} onChange={toggleAll} />
              {term ? '(Select All Search Results)' : '(Select All)'}
            </label>
            {shown.map(o => (
              <label key={o.key} className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-slate-50">
                <input type="checkbox" checked={draft.has(o.key)} onChange={() => toggle(o.key)} />
                <span className={`truncate ${o.key === '' ? 'italic text-slate-400' : ''}`} title={o.label}>
                  {o.key === '' ? '(Blanks)' : o.label}
                </span>
              </label>
            ))}
            {shown.length === 0 && <div className="px-2 py-2 text-slate-400">No matches</div>}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            {active ? (
              <button type="button" className="text-slate-500 underline hover:text-red-600" onClick={() => { onChange(null); setOpen(false); }}>
                Clear filter
              </button>
            ) : <span />}
            <div className="flex gap-1">
              <button type="button" className="btn-primary px-3 py-1 text-xs" disabled={picked.length === 0} onClick={apply}>OK</button>
              <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
